import {
  formatPartConstraintDimension,
  resolvePartConstraintUnit,
  SERVICE_EOI_DIMENSION_UNITS,
  SERVICE_EOI_WEIGHT_UNITS,
} from "../serviceEoiDetails";

/** @typedef {import("../serviceEoiDetails").ServiceEoiDetails} ServiceEoiDetails */

/** @typedef {"maxLength"|"maxWidth"|"maxHeight"|"maxWeight"} EoiUpdatableParameterKey */

/** @type {readonly EoiUpdatableParameterKey[]} */
export const EOI_UPDATABLE_PARAMETER_KEYS = [
  "maxLength",
  "maxWidth",
  "maxHeight",
  "maxWeight",
];

/** @type {Record<EoiUpdatableParameterKey, string>} */
export const EOI_PARAMETER_LABEL_KEYS = {
  maxLength: "serviceEoiMaxLength",
  maxWidth: "serviceEoiMaxWidth",
  maxHeight: "serviceEoiMaxHeight",
  maxWeight: "serviceEoiMaxWeight",
};

/** @type {Record<EoiUpdatableParameterKey, string>} */
const EOI_PARAMETER_UNIT_KEYS = {
  maxLength: "maxLengthUnit",
  maxWidth: "maxWidthUnit",
  maxHeight: "maxHeightUnit",
  maxWeight: "maxWeightUnit",
};

/**
 * @typedef {Object} CatalogMilestoneParameterUpdate
 * @property {string} parameterKey
 * @property {string} [value]
 * @property {string} [unit]
 * @property {boolean} [completed]
 */

/**
 * @typedef {Object} EoiUpdatableParameterOption
 * @property {EoiUpdatableParameterKey} parameterKey
 * @property {string} labelKey
 * @property {string} eoiDisplay
 * @property {string} [eoiValue]
 * @property {string} [eoiUnit]
 */

/**
 * @param {EoiUpdatableParameterKey} parameterKey
 */
export function unitOptionsForEoiParameter(parameterKey) {
  return parameterKey === "maxWeight"
    ? [...SERVICE_EOI_WEIGHT_UNITS]
    : [...SERVICE_EOI_DIMENSION_UNITS];
}

/**
 * @param {unknown} raw
 * @returns {CatalogMilestoneParameterUpdate}
 */
export function normalizeCatalogParameterUpdate(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return { parameterKey: "" };
  }

  const record = /** @type {Record<string, unknown>} */ (raw);
  const parameterKey = String(
    record.parameterKey ?? record.parameter_key ?? ""
  ).trim();

  /** @type {CatalogMilestoneParameterUpdate} */
  const row = { parameterKey };

  const value = String(record.value ?? "").trim();
  const unit = String(record.unit ?? "").trim();
  if (value) row.value = value;
  if (unit) row.unit = unit;
  if (record.completed === true || record.done === true) {
    row.completed = true;
  }

  return row;
}

/**
 * @param {CatalogMilestoneParameterUpdate} row
 */
export function isEoiParameterUpdateComplete(row) {
  return Boolean(String(row?.value ?? "").trim());
}

/**
 * @param {ServiceEoiDetails|null|undefined} eoiDetails
 * @returns {EoiUpdatableParameterOption[]}
 */
export function listEoiUpdatableParameters(eoiDetails) {
  const partConstraints = eoiDetails?.partConstraints;
  if (!partConstraints) return [];

  /** @type {EoiUpdatableParameterOption[]} */
  const options = [];

  for (const parameterKey of EOI_UPDATABLE_PARAMETER_KEYS) {
    const unitKey = EOI_PARAMETER_UNIT_KEYS[parameterKey];
    const eoiDisplay = formatPartConstraintDimension(
      partConstraints,
      parameterKey,
      unitKey
    );
    if (!eoiDisplay) continue;

    const value = String(partConstraints[parameterKey] ?? "").trim();
    const unit = resolvePartConstraintUnit(partConstraints, parameterKey, unitKey);

    options.push({
      parameterKey,
      labelKey: EOI_PARAMETER_LABEL_KEYS[parameterKey],
      eoiDisplay,
      eoiValue: value,
      eoiUnit: unit,
    });
  }

  return options;
}

/**
 * @param {(key: string, options?: Record<string, string>) => string} t
 * @param {string} parameterKey
 */
export function getEoiParameterDimensionLabel(t, parameterKey) {
  const labelKey =
    EOI_PARAMETER_LABEL_KEYS[/** @type {EoiUpdatableParameterKey} */ (parameterKey)];
  return labelKey ? t(labelKey) : parameterKey;
}

/**
 * @param {(key: string, options?: Record<string, string>) => string} t
 * @param {string} parameterKey
 */
export function getUpdatedParameterLabel(t, parameterKey) {
  const dimensionLabel = getEoiParameterDimensionLabel(t, parameterKey);
  return t("catalogUpdatedParameterLabel", { dimension: dimensionLabel });
}

/**
 * All four final-milestone dimension fields (mandatory when completing the service).
 *
 * @param {ServiceEoiDetails|null|undefined} eoiDetails
 * @returns {Array<EoiUpdatableParameterOption & { defaultUnit: string }>}
 */
export function listFinalMilestoneParameterDefinitions(eoiDetails) {
  const partConstraints = eoiDetails?.partConstraints;

  return EOI_UPDATABLE_PARAMETER_KEYS.map((parameterKey) => {
    const unitKey = EOI_PARAMETER_UNIT_KEYS[parameterKey];
    const eoiDisplay = partConstraints
      ? formatPartConstraintDimension(partConstraints, parameterKey, unitKey)
      : "";
    const eoiValue = partConstraints
      ? String(partConstraints[parameterKey] ?? "").trim()
      : "";
    const lockedUnit = partConstraints
      ? resolvePartConstraintUnit(partConstraints, parameterKey, unitKey)
      : parameterKey === "maxWeight"
        ? "kg"
        : "mm";

    return {
      parameterKey,
      labelKey: EOI_PARAMETER_LABEL_KEYS[parameterKey],
      eoiDisplay,
      eoiValue,
      eoiUnit: lockedUnit,
      defaultUnit: lockedUnit,
    };
  });
}

/**
 * @param {CatalogMilestoneParameterUpdate[]} [existing]
 * @param {ServiceEoiDetails|null|undefined} eoiDetails
 * @returns {CatalogMilestoneParameterUpdate[]}
 */
export function ensureFinalMilestoneParameterUpdates(existing = [], eoiDetails) {
  return listFinalMilestoneParameterDefinitions(eoiDetails).map((definition) => {
    const prior = existing.find(
      (row) => row.parameterKey === definition.parameterKey
    );
    return {
      parameterKey: definition.parameterKey,
      ...(prior?.value ? { value: prior.value } : {}),
      unit: definition.defaultUnit,
      ...(prior?.completed ? { completed: prior.completed } : {}),
    };
  });
}

/**
 * @param {EoiUpdatableParameterOption} definition
 */
export function getEoiOriginalDisplayForComparison(definition) {
  const eoiValue = String(definition?.eoiValue ?? "").trim();
  if (!eoiValue) return "";
  return String(definition?.eoiDisplay ?? "").trim();
}

/**
 * @param {EoiUpdatableParameterOption} definition
 * @param {CatalogMilestoneParameterUpdate} row
 */
export function areEoiAndUpdatedValuesEqual(definition, row) {
  const eoiValue = String(definition?.eoiValue ?? "").trim();
  const updatedValue = String(row?.value ?? "").trim();
  if (!eoiValue || !updatedValue) return false;

  const eoiNumeric = Number(eoiValue);
  const updatedNumeric = Number(updatedValue);

  if (Number.isFinite(eoiNumeric) && Number.isFinite(updatedNumeric)) {
    return eoiNumeric === updatedNumeric;
  }

  return eoiValue === updatedValue;
}

/**
 * @param {CatalogMilestoneParameterUpdate} row
 */
export function formatParameterValueDisplay(row) {
  const value = String(row?.value ?? "").trim();
  if (!value) return "";
  const unit = String(row?.unit ?? "").trim();
  return unit ? `${value} ${unit}` : value;
}

